import React, { memo, useCallback, useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { RichText } from '@web/web5';
import { Popover, Chip, Icon, Advice } from 'hoi-poi-ui';
import InsertFieldTool from './InsertFieldTool';

import './styles.scss';

const InputTemplated = memo(
    ({ insertFieldOptions, value, onChange, placeholder, error, infoPopover }) => {
        const rootRef = useRef(null);
        const [isFocused, setIsFocused] = useState(false);
        const editor = useRef(null);
        const insertFieldMenuRef = useRef(null);

        const handleChange = useCallback(
            (v) => {
                onChange && onChange(v);
            },
            [onChange],
        );

        const getEditorRef = useCallback((ref) => {
            editor.current = ref;
        }, []);

        const onInsertField = useCallback((value) => {
            setIsFocused(true);
            editor.current
                .chain()
                .insertContent(value?.value, {
                    updateSelection: true,
                    parseOptions: {
                        preserveWhitespace: 'full',
                    },
                })
                .focus()
                .run();
        }, []);

        const editorProps = useMemo(
            () => ({
                value,
                hideClear: true,
                isFullWidth: true,
                withCustomToolbar: true,
                className: 'fm-automation__input-templated__input',
                placeholder,
                onChange: (value) => {
                    handleChange(value.text);
                },
                getEditorRef,
                overrides: {
                    editor: { style: { height: '100%' } },
                    editorWrapper: {
                        style: {
                            backgroundColor: 'transparent',
                            borderColor: 'transparent',
                            borderRadius: '0 0 8px 8px',
                            height: '100%',
                        },
                    },
                },
            }),
            [value, placeholder, getEditorRef, handleChange],
        );

        useEffect(() => {
            const handleRootFocus = (e) => {
                if (
                    rootRef?.current?.contains(e.target) ||
                    insertFieldMenuRef.current?.contains(e.target)
                ) {
                    setIsFocused(true);
                } else {
                    setIsFocused(false);
                }
            };

            document.addEventListener('mousedown', handleRootFocus);
            return () => {
                document.removeEventListener('mousedown', handleRootFocus);
            };
        }, []);

        const popoverProps = useMemo(
            () => ({
                content: infoPopover,
                overlayStyle: { zIndex: 9999999 },
            }),
            [infoPopover],
        );

        return (
            <div
                ref={rootRef}
                className={classnames('fm-automation__input-templated', {
                    'fm-automation__input-templated--focused': isFocused,
                })}
            >
                <div className="fm-automation__input-templated__toolbar">
                    <InsertFieldTool
                        insertFieldOptions={insertFieldOptions}
                        onChange={onInsertField}
                        ref={insertFieldMenuRef}
                    />
                    {infoPopover && (
                        <Popover {...popoverProps}>
                            <Chip size="large">
                                <Icon name="info" />
                            </Chip>
                        </Popover>
                    )}
                </div>
                <RichText {...editorProps} />
                {error && (
                    <Advice
                        className="fm-automation__input-templated__error"
                        type="error"
                        defaultCollapsed={false}
                        showIcon
                    >
                        {error}
                    </Advice>
                )}
            </div>
        );
    },
);

InputTemplated.displayName = 'InputTemplated';

export default InputTemplated;
